import React from "react";
import { Skeleton, Tooltip, Dropdown } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "components/commonComponents/Loader/Index";

// Status configuration with Tailwind classes
const STATUS_CONFIG = {
  DELETED:   { label: 'Deleted',   className: 'bg-red-50 text-red-600' },
  NEW:       { label: 'New',       className: 'bg-gray-100 text-gray-600' },
  DRAFT:     { label: 'Draft',     className: 'bg-gray-100 text-gray-600' },
  DEPRECATED:{ label: 'Deprecated',className: 'bg-orange-50 text-orange-600' },
  THROTTLED: { label: 'Throttled', className: 'bg-yellow-50 text-yellow-600' },
  DISABLED:  { label: 'Disabled',  className: 'bg-red-50 text-red-600' },
  BLOCKED:   { label: 'Blocked',   className: 'bg-red-50 text-red-600' },
  TAMPERED:  { label: 'Tampered',  className: 'bg-pink-50 text-pink-600' },
  PUBLISHED: { label: 'Published', className: 'bg-green-50 text-green-600' },
  // fallback
  __DEFAULT__: { label: 'Unknown', className: 'bg-gray-100 text-gray-600' }
};

const headerList = [
  { id: 1, label: "Flow Name",     width: '30%' },
  { id: 2, label: "Flow ID",       width: '25%' },
  { id: 3, label: "Status",        width: '20%' },
  { id: 4, label: "Last Updated",  width: '15%' },
  { id: 5, label: "Actions",       width: '10%' },
];

const GRID_TEMPLATE = '30% 25% 20% 15% 10%';

const FlowList = ({
  flows = [],
  isLoading,
  isLoadingNextPage,
  hasMore = true,
  loadNextPage,
  onFlowSelect,
  onDeleteFlow,
  onDeprecateFlow,
  actionInProgressId,
}) => {
  const length = flows?.length || 0;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    // ❌ don’t create a new scroll context here
    <div className={`w-full flex-1 flex-col bg-[#f4f6f8] ${isLoading ? 'px-3' : ''}`}>
      <div className="w-full mt-4">
        <Skeleton loading={isLoading} active paragraph={{ rows: 11 }}>
          {/* Sticky Header (sticks to page top while window scrolls) */}
          <div
            className="sticky top-[-23px] z-10 bg-[#f4f6f8] border-b border-gray-200"
            style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04)' }}
          >
            <div
              className="grid gap-2 px-4 py-3 text-sm font-medium text-gray-700"
              style={{ gridTemplateColumns: GRID_TEMPLATE }}
            >
              {headerList.map((item) => (
                <div key={item.id} className="truncate">{item.label}</div>
              ))}
            </div>
          </div>

          <InfiniteScroll
            dataLength={length}
            next={loadNextPage}
            hasMore={hasMore}
            loader={null}
            // ✅ remove scrollableTarget so it uses the window
            className="space-y-3 px-1"
          >
            {length > 0 ? (
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {flows.map((flow, index) => {
                    const statusCfg = STATUS_CONFIG[flow.status] || STATUS_CONFIG.__DEFAULT__;
                    const flowKey = flow?.id ?? flow?.flow_id;
                    const flowKeyString =
                      flowKey !== undefined && flowKey !== null
                        ? String(flowKey)
                        : undefined;
                    const isActionLoading = Boolean(
                      actionInProgressId &&
                        flowKeyString &&
                        actionInProgressId === flowKeyString
                    );
                    const isDeprecated = flow.status === "DEPRECATED";
                    const menuItems = [];

                    if (flow.status === "PUBLISHED") {
                      menuItems.push({
                        key: "deprecate",
                        label: "Deprecate",
                      });
                    }

                    if (flow.status === "DRAFT") {
                      menuItems.push({
                        key: "delete",
                        label: "Delete",
                      });
                    }

                    const handleMenuClick = ({ key, domEvent }) => {
                      domEvent?.stopPropagation?.();
                      if (key === "deprecate") {
                        onDeprecateFlow?.(flow);
                      } else if (key === "delete") {
                        onDeleteFlow?.(flow);
                      }
                    };
                    const isMenuDisabled =
                      isDeprecated || isActionLoading || menuItems.length === 0;

                    return (
                      <div
                        key={flowKeyString ?? `flow-${index}`}
                        className="grid items-center gap-2 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        style={{ gridTemplateColumns: GRID_TEMPLATE }}
                        onClick={() => onFlowSelect?.(flow)}
                      >
                        {/* Flow Name + Categories */}
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {flow.name || 'Unnamed Flow'}
                          </div>
                          {flow.category_names?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1 truncate">
                              {flow.category_names.join(', ')}
                            </div>
                          )}
                        </div>

                        {/* Flow ID */}
                        <div className="min-w-0">
                          <Tooltip title={flow.flow_id}>
                            <span className="text-gray-600 font-mono text-sm truncate">
                              {flow.flow_id || '-'}
                            </span>
                          </Tooltip>
                        </div>

                        {/* Status */}
                        <div className="min-w-0">
                          {flow.status && (
                            <div
                              className={`w-fit h-fit rounded-md px-2.5 py-0.5 font-medium text-xs ${statusCfg.className}`}
                            >
                              {statusCfg.label}
                            </div>
                          )}
                        </div>

                        {/* Last Updated */}
                        <div className="text-gray-600 whitespace-nowrap">
                          {formatDate(flow.updated_at)}
                        </div>

                        {/* Actions */}
                        <div className="justify-self-center">
                          <Dropdown
                            menu={{
                              items: menuItems,
                              onClick: handleMenuClick,
                            }}
                            trigger={["click"]}
                            disabled={isMenuDisabled}
                          >
                            <button
                              className={`text-gray-400 transition-colors ${
                                isMenuDisabled
                                  ? "cursor-not-allowed opacity-50"
                                  : "hover:text-blue-600"
                              }`}
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Flow actions"
                              disabled={isMenuDisabled}
                            >
                              <svg
                                className={`w-5 h-5 ${
                                  isActionLoading ? "animate-spin" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <circle cx="12" cy="12" r="1" fill="currentColor" />
                                <circle cx="12" cy="5" r="1" fill="currentColor" />
                                <circle cx="12" cy="19" r="1" fill="currentColor" />
                              </svg>
                            </button>
                          </Dropdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center bg-white rounded-lg min-h-[30vh]">
                <div className="text-center p-6">
                  <p className="text-gray-500">No flows found</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Try adjusting your search or create a new flow
                  </p>
                </div>
              </div>
            )}
          </InfiniteScroll>

          {isLoadingNextPage && (
            <div className="w-full py-6 flex items-center justify-center bg-white rounded-lg mt-3">
              <Loader Width={30} Height={30} loaderBg="white" />
            </div>
          )}
        </Skeleton>
      </div>
    </div>
  );
};

export default FlowList;
